import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchGallerierFromCMS, FALLBACK_GALLERI } from '../lib/cms';
import LightboxModal from '../components/LightboxModal';

export default function Projects() {
  const [gallerier, setGallerier] = useState(FALLBACK_GALLERI);
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      const cmsGallerier = await fetchGallerierFromCMS();
      if (cmsGallerier) setGallerier(cmsGallerier);
    }
    loadData();
  }, []);

  const categories = ['Alle', ...new Set(gallerier.map((g) => g.category).filter(Boolean))];

  const filteredItems = activeCategory === 'Alle'
    ? gallerier
    : gallerier.filter((g) => g.category === activeCategory);

  const openLightbox = (galleryItem, imgIndex = 0) => {
    setSelectedGallery(galleryItem);
    setCurrentImgIndex(imgIndex);
    setLightboxOpen(true);
  };

  return (
    <div className="bg-[#dce2d7] text-[#1f241d]">
      <Helmet>
        <title>Projekter & Galleri - PR Entreprise & Anlæg ApS</title>
        <meta
          name="description"
          content="Se billeder og eksempler på vores udførte arbejde inden for autoriseret kloak, belægning, haveanlæg, indkørsler og jordarbejde i Ishøj og omegn."
        />
        <meta property="og:title" content="Projekter & Galleri - PR Entreprise & Anlæg ApS" />
      </Helmet>

      {/* Header */}
      <section className="relative bg-[#141f14] text-white py-16 lg:py-20 border-b border-[#233323] overflow-hidden">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/belaegning-1.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141f14] via-[#141f14]/90 to-transparent pointer-events-none" />
        <div className="relative max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4 animate-fade-in-up">
            <span className="text-[#9ecc49] text-xs font-bold uppercase tracking-widest bg-[#3d5229] px-3 py-1 rounded-none border border-[#9ecc49]/30">
              Projekter & Reference-Galleri
            </span>
            <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Udførte Anlæg, Kloak & Entrepriser
            </h1>
            <p className="text-[#e8eee6] text-base sm:text-lg leading-relaxed">
              Udforsk et udvalg af vores færdiggjorte anlægs- og belægningsprojekter for private og erhvervskunder. Klik på et billede for at åbne galleriet i stor visning.
            </p>
          </div>
        </div>
      </section>

      {/* Main Gallery Section */}
      <section className="py-16 bg-[#dce2d7] text-[#1f241d]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category filter buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-none text-xs font-heading font-bold tracking-wider uppercase transition-all ${
                  activeCategory === cat
                    ? 'bg-[#3d5229] text-[#9ecc49] shadow-md border border-[#9ecc49]/50 scale-105'
                    : 'bg-white text-[#1f241d] hover:bg-[#cbd3c5] border border-[#cbd3c5]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Gallery Items Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => openLightbox(item, 0)}
                className="group bg-white rounded-none overflow-hidden border border-[#cbd3c5] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
              >
                <div className="relative h-60 overflow-hidden bg-gray-900">
                  <img
                    src={item.images && item.images[0] ? `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${item.images[0].replace('https://assets.auxodev.dk/', '')}` : item.images[0]}
                    alt={item.alt_text || item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="bg-[#9ecc49] text-[#1f241d] text-xs font-bold px-3.5 py-2 rounded-none shadow flex items-center gap-2">
                      <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                      Åbn i stor visning
                    </span>
                  </div>

                  {item.category && (
                    <span className="absolute top-3 left-3 bg-[#3d5229] text-white text-[11px] font-semibold px-2.5 py-1 rounded-none border border-[#9ecc49]/30">
                      {item.category}
                    </span>
                  )}
                </div>

                <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h2 className="font-heading font-bold text-lg text-[#1f241d] group-hover:text-[#3d5229] transition-colors">
                      {item.title}
                    </h2>
                    <p className="text-[#4e564a] text-xs leading-relaxed mt-2 line-clamp-2">
                      {item.caption || item.description}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-[#cbd3c5] flex items-center justify-between text-xs font-bold text-[#3d5229]">
                    <span className="flex items-center gap-1.5">
                      <i className="fa-solid fa-images" aria-hidden="true" />
                      <span>{item.images?.length || 1} billeder</span>
                    </span>
                    <span className="text-[#3d5229] group-hover:translate-x-1 transition-transform">Vis galleri &rarr;</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Lightbox Component */}
      <LightboxModal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={selectedGallery?.images || []}
        currentIndex={currentImgIndex}
        setCurrentIndex={setCurrentImgIndex}
        caption={selectedGallery?.caption || selectedGallery?.description}
        title={selectedGallery?.title}
      />
    </div>
  );
}
